/**
 * Kanban board para pipelines segmentadas com drag-and-drop
 */

import React, { useRef, useMemo, useState, useCallback, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Empty, message, Tooltip } from 'antd';
import { formatDate, formatPhone } from '@/lib/utils';
import { formatAgendamentoCard } from '../utils/formatAgendamentoCard';
import { usePipelineBoard } from '../hooks/usePipelineBoard';
import { useProductionStartDay } from '@/hooks/useProductionStartDay';
import { useCardDropActions, DropZoneAction } from '../hooks/useCardDropActions';
import {
  useTransferredFromPipeline,
  buildVendaHistoryStage,
} from '../hooks/useTransferredFromPipeline';
import { usePipelineLeadUrl } from '../hooks/usePipelineLeadUrl';
import { usePipelineTags } from '../hooks/usePipelineTags';
import { PipelineDragFooter, getDragFooterZones } from './PipelineDragFooter';
import { PipelineLeadModal, EMPTY_LEAD_TAGS } from './PipelineLeadModal';
import { PipelineFilterSummary, PipelineBoardView, isOutcomeStageName } from './PipelineFilterBar';
import { PipelineCard, StageWithCards, KanbanBoard, PipelineTag } from '../types';
import { matchesGrupoCota, searchMatchesGrupoOuCota } from '../utils/grupoCotaFilter';

const EMPTY_TRANSFERRED_CARDS: PipelineCard[] = [];

interface PipelineBoardKanbanProps {
  pipelineId: string;
  onCardClick?: (card: PipelineCard) => void;
  embedded?: boolean;
  search?: string;
  tagFilter?: string[];
  tenantFilter?: number[];
  assembleiaVencimentos?: string[];
  grupoFilter?: string;
  cotaFilter?: string;
  boardView?: PipelineBoardView;
  onSummaryChange?: (summary: PipelineFilterSummary) => void;
  productionPeriodFilter?: { month: number; year: number } | null;
  startDay?: number;
}

const EMPTY_TAG_FILTER: string[] = [];
const EMPTY_TENANT_FILTER: number[] = [];
const EMPTY_ASSEMBLEIA_FILTER: string[] = [];

function matchesAssembleiaVencimento(
  dataVencimento: string | null | undefined,
  vencimentos: string[]
): boolean {
  if (vencimentos.length === 0) return true;
  if (!dataVencimento) return false;
  return vencimentos.includes(dataVencimento.slice(0, 10));
}

function findCardInBoard(board: KanbanBoard, cardId: string): PipelineCard | null {
  for (const stageWithCards of board.stages) {
    const card = stageWithCards.cards.find((c) => c.id === cardId);
    if (card) return card;
  }
  return null;
}

function cardValue(card: PipelineCard): number {
  const fromContrato = Number(card.contratos?.valor_bem ?? 0);
  if (fromContrato > 0) return fromContrato;
  return Number(card.dados_card?.valor ?? 0) || 0;
}

export function filterStagesByView(
  stages: StageWithCards[],
  boardView: PipelineBoardView
): StageWithCards[] {
  switch (boardView) {
    case 'fixados':
      return stages.filter((swc) => swc.stage.nome.toLowerCase().includes('fixado'));
    case 'venda':
      // Qualificação: cards transferidos vivem na Comercial — visão montada via histórico
      return [];
    case 'perdidos':
      return stages.filter((swc) => {
        const nome = swc.stage.nome.toLowerCase();
        return nome.includes('perdido') || nome.includes('perda');
      });
    case 'todos':
      return stages;
    case 'ativos':
    default:
      return stages.filter((swc) => !isOutcomeStageName(swc.stage.nome, swc.stage.oculta));
  }
}

export function PipelineBoardKanban({
  pipelineId,
  onCardClick,
  embedded = false,
  search = '',
  tagFilter = EMPTY_TAG_FILTER,
  tenantFilter = EMPTY_TENANT_FILTER,
  assembleiaVencimentos = EMPTY_ASSEMBLEIA_FILTER,
  grupoFilter = '',
  cotaFilter = '',
  boardView = 'ativos',
  onSummaryChange,
  productionPeriodFilter,
  startDay: startDayProp,
}: PipelineBoardKanbanProps) {
  const startDayFromTenant = useProductionStartDay();
  const startDay = startDayProp ?? startDayFromTenant;
  const { kanbanBoard, loading, error, moveCardAsync } = usePipelineBoard(
    pipelineId,
    productionPeriodFilter,
    startDay
  );
  const isVendaHistoryView = boardView === 'venda';
  const {
    data: transferredCards = EMPTY_TRANSFERRED_CARDS,
    isLoading: loadingTransferred,
  } = useTransferredFromPipeline(pipelineId, isVendaHistoryView);
  const { applyAction } = useCardDropActions(pipelineId);
  const { leadId, openLead, closeLead } = usePipelineLeadUrl(pipelineId);
  const { tagsById } = usePipelineTags();
  const containerRef = useRef<HTMLDivElement>(null);

  const [draggingCard, setDraggingCard] = useState<PipelineCard | null>(null);
  const [activeZone, setActiveZone] = useState<DropZoneAction | null>(null);
  const [dragOverStageId, setDragOverStageId] = useState<string | null>(null);
  const [isMoving, setIsMoving] = useState(false);

  const footerZones = useMemo(
    () => getDragFooterZones(kanbanBoard?.pipeline?.tipo),
    [kanbanBoard?.pipeline?.tipo]
  );

  const filteredBoard = useMemo(() => {
    if (!kanbanBoard) return kanbanBoard;

    const term = search.trim().toLowerCase();
    let stages: StageWithCards[];

    if (isVendaHistoryView) {
      stages = [buildVendaHistoryStage(transferredCards)];
    } else {
      stages = filterStagesByView(kanbanBoard.stages, boardView);
    }

    if (
      term ||
      tagFilter.length > 0 ||
      tenantFilter.length > 0 ||
      assembleiaVencimentos.length > 0 ||
      grupoFilter.trim() ||
      cotaFilter.trim()
    ) {
      stages = stages.map((swc) => {
        const cards = swc.cards.filter((card) => {
          const dados = card.dados_card || {};
          const cardTagIds = Array.isArray(dados.tags) ? (dados.tags as string[]) : [];

          const matchesSearch =
            !term ||
            String(dados.nome || '').toLowerCase().includes(term) ||
            String(dados.telefone || '').toLowerCase().includes(term) ||
            String(dados.interesse || '').toLowerCase().includes(term) ||
            cardTagIds.some((id) => (tagsById.get(id)?.nome || '').toLowerCase().includes(term)) ||
            searchMatchesGrupoOuCota(card, term);

          const matchesTagFilter =
            tagFilter.length === 0 || tagFilter.some((id) => cardTagIds.includes(id));

          const matchesTenantFilter =
            tenantFilter.length === 0 || tenantFilter.includes(card.tenant_id);

          const matchesAssembleia = matchesAssembleiaVencimento(
            card.contratos?.grupos?.data_vencimento_grupo,
            assembleiaVencimentos
          );

          return (
            matchesSearch &&
            matchesTagFilter &&
            matchesTenantFilter &&
            matchesAssembleia &&
            matchesGrupoCota(card, grupoFilter, cotaFilter)
          );
        });
        return { ...swc, cards, count: cards.length };
      });
    }

    const totalCards = stages.reduce((sum, s) => sum + s.count, 0);
    return { ...kanbanBoard, stages, totalCards };
  }, [
    kanbanBoard,
    search,
    tagFilter,
    tenantFilter,
    assembleiaVencimentos,
    grupoFilter,
    cotaFilter,
    tagsById,
    boardView,
    isVendaHistoryView,
    transferredCards,
  ]);

  useEffect(() => {
    if (!filteredBoard || !onSummaryChange) return;
    const cards = filteredBoard.stages.flatMap((s) => s.cards);
    const count = cards.length;
    const value = cards.reduce((sum, card) => sum + cardValue(card), 0);
    onSummaryChange({ count, value });
  }, [filteredBoard, onSummaryChange]);

  const selectedCard = useMemo(() => {
    if (!leadId) return null;
    if (filteredBoard) {
      const fromView = findCardInBoard(filteredBoard, leadId);
      if (fromView) return fromView;
    }
    if (kanbanBoard) {
      return findCardInBoard(kanbanBoard, leadId);
    }
    return null;
  }, [leadId, filteredBoard, kanbanBoard]);

  const modalOpen = !!selectedCard;

  const boardLoading = loading || (isVendaHistoryView && loadingTransferred);

  useEffect(() => {
    if (!leadId || boardLoading || !kanbanBoard) return;
    if (!selectedCard) {
      closeLead?.();
    }
  }, [leadId, boardLoading, kanbanBoard, selectedCard]);

  const handleDragStart = useCallback((card: PipelineCard) => {
    if (isVendaHistoryView) return;
    setDraggingCard(card);
  }, [isVendaHistoryView]);

  const handleDragEnd = useCallback(() => {
    setDraggingCard(null);
    setActiveZone(null);
    setDragOverStageId(null);
  }, []);

  const handleStageDrop = useCallback(
    async (targetStageId: string) => {
      if (!draggingCard || draggingCard.stage_id === targetStageId) {
        handleDragEnd();
        return;
      }

      const cardId = draggingCard.id;
      const stagAnteriorId = draggingCard.stage_id;
      handleDragEnd();

      try {
        setIsMoving(true);
        await moveCardAsync({
          cardId,
          pipelineId,
          stageId: targetStageId,
          stagAnteriorId,
        });
      } catch (error) {
        message.error('Erro ao mover card. A alteração foi desfeita.');
      } finally {
        setIsMoving(false);
      }
    },
    [draggingCard, pipelineId, moveCardAsync, handleDragEnd]
  );

  const handleFooterDrop = useCallback(
    async (zone: DropZoneAction) => {
      if (!draggingCard) return;

      try {
        await applyAction.mutateAsync({ card: draggingCard, action: zone });
        const labels: Record<DropZoneAction, string> = {
          excluir: 'Card excluído',
          perdido: 'Marcado como perdido',
          fixado: 'Marcado como fixado',
          venda: 'Transferido para Comercial — Aguardando contrato',
        };
        message.success(labels[zone]);
      } catch (err) {
        message.error(err instanceof Error ? err.message : 'Erro ao processar ação');
      } finally {
        handleDragEnd();
      }
    },
    [draggingCard, applyAction, handleDragEnd]
  );

  const handleCardClick = useCallback(
    (card: PipelineCard) => {
      openLead(card.id);
      onCardClick?.(card);
    },
    [onCardClick, openLead]
  );

  if (boardLoading) {
    return (
      <div className={`flex flex-col items-center justify-center gap-4 ${embedded ? 'h-full' : 'h-64'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-[#FF3737]" />
        <p className="text-sm text-gray-600 dark:text-gray-400">Carregando pipeline...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-200 p-6">
        <p className="text-sm text-red-800">{error.message}</p>
      </div>
    );
  }

  if (!filteredBoard || filteredBoard.stages.length === 0 || (isVendaHistoryView && filteredBoard.totalCards === 0)) {
    const emptyByView: Record<PipelineBoardView, string> = {
      ativos: 'Nenhum lead ativo nesta visão.',
      venda: 'Nenhum card transferido para Comercial nesta visão.',
      fixados: 'Nenhum card fixado nesta visão.',
      perdidos: 'Nenhum card perdido nesta visão.',
      todos: 'Nenhuma etapa configurada. Adicione stages a esta pipeline.',
    };
    return (
      <div className="h-full flex items-center justify-center">
        <Empty description={emptyByView[boardView]} />
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${embedded ? 'h-full' : 'space-y-4'}`}>
      <div
        ref={containerRef}
        className={`overflow-x-auto overflow-y-hidden ${embedded ? 'flex-1 min-h-0' : 'h-[calc(100vh-280px)] min-h-[400px]'}`}
      >
        <div className="inline-flex gap-3 h-full pb-2" style={{ minWidth: 'min-content' }}>
          {filteredBoard.stages.map((stageWithCards, index) => (
            <div key={stageWithCards.stage.id} className="transition-all duration-300 ease-in-out">
              <KanbanColumn
                pipelineId={pipelineId}
                stageWithCards={stageWithCards}
                showQuickAdd={index === 0 && !stageWithCards.stage.oculta}
                isDragOver={dragOverStageId === stageWithCards.stage.id}
                tagsById={tagsById}
                onCardClick={handleCardClick}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={() => setDragOverStageId(stageWithCards.stage.id)}
                onDragLeave={() => setDragOverStageId(null)}
                onDrop={() => handleStageDrop(stageWithCards.stage.id)}
              />
            </div>
          ))}
        </div>
      </div>

      {draggingCard && !isVendaHistoryView && footerZones.length > 0 && (
        <PipelineDragFooter
          zones={footerZones}
          activeZone={activeZone}
          onDragOver={setActiveZone}
          onDragLeave={() => setActiveZone(null)}
          onDrop={handleFooterDrop}
        />
      )}

      {/* Modal de detalhes do cliente */}
      {selectedCard && filteredBoard && (
        <PipelineLeadModal
          open={modalOpen}
          pipelineId={pipelineId}
          stageId={selectedCard.stage_id}
          stageName={filteredBoard.stages.find(s => s.stage.id === selectedCard.stage_id)?.stage.nome}
          clienteId={selectedCard.cliente_id?.toString()}
          cardId={selectedCard.id}
          dadosCard={selectedCard.dados_card}
          initialTags={
            Array.isArray(selectedCard.dados_card?.tags)
              ? (selectedCard.dados_card.tags as string[])
              : EMPTY_LEAD_TAGS
          }
          agendamentoId={
            typeof selectedCard.dados_card?.agendamento_id === 'number'
              ? selectedCard.dados_card.agendamento_id
              : undefined
          }
          leadName={String(selectedCard.dados_card?.nome || '')}
          card={selectedCard}
          onClose={closeLead}
          onLeadDeleted={closeLead}
        />
      )}
    </div>
  );
}

function KanbanColumn({
  pipelineId,
  stageWithCards,
  showQuickAdd,
  isDragOver,
  tagsById,
  onCardClick,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  pipelineId: string;
  stageWithCards: StageWithCards;
  showQuickAdd: boolean;
  isDragOver: boolean;
  tagsById: Map<string, PipelineTag>;
  onCardClick?: (card: PipelineCard) => void;
  onDragStart: (card: PipelineCard) => void;
  onDragEnd: () => void;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: () => void;
}) {
  const { stage, cards, count } = stageWithCards;
  const accentColor = stage.cor || '#FF3737';
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  return (
    <>
    <div
      className={`rounded-xl bg-white dark:bg-zinc-900 border flex flex-col flex-shrink-0 h-full shadow-sm transition-all duration-300 ease-in-out overflow-hidden ${
        isDragOver
          ? 'border-[#FF3737] ring-2 ring-[#FF3737]/30 shadow-lg scale-105'
          : 'border-gray-200 dark:border-zinc-800'
      }`}
      style={{ width: '300px' }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOver();
      }}
      onDragLeave={onDragLeave}
      onDrop={(e) => {
        e.preventDefault();
        onDrop();
      }}
    >
      <div className="flex-shrink-0">
        <div className="h-1 rounded-t-xl" style={{ backgroundColor: accentColor }} />
        <div className="px-4 py-3 border-b border-gray-100 dark:border-zinc-800 bg-gray-50/50 dark:bg-zinc-800/30">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div
                className="w-2 h-6 rounded-full flex-shrink-0"
                style={{ backgroundColor: accentColor }}
              />
              <h2 className="font-bold text-sm text-gray-800 dark:text-gray-100 truncate">
                {stage.nome}
              </h2>
            </div>
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-zinc-700 px-2.5 py-1 rounded-full flex-shrink-0">
              {count}
            </span>
          </div>
        </div>
      </div>

      <div className="p-3 space-y-2.5 flex-1 overflow-y-auto min-h-0 bg-gray-50/30 dark:bg-zinc-800/10">
        {showQuickAdd && (
          <button
            type="button"
            onClick={() => setQuickAddOpen(true)}
            className="w-full border-2 border-dashed border-gray-300 dark:border-zinc-600 rounded-lg px-3 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-400 dark:hover:border-zinc-500 transition-all text-center bg-white/50 dark:bg-zinc-700/20"
          >
            + Novo card
          </button>
        )}

        {cards.length === 0 ? (
          <div className="flex items-center justify-center h-24">
            <p className="text-xs text-gray-400 dark:text-gray-500">Nenhum card</p>
          </div>
        ) : (
          cards.map((card) => (
            <PipelineCardItem
              key={card.id}
              card={card}
              stageName={stage.nome}
              tagsById={tagsById}
              onClick={() => onCardClick?.(card)}
              onDragStart={() => onDragStart(card)}
              onDragEnd={onDragEnd}
              accentColor={accentColor}
            />
          ))
        )}
      </div>
    </div>

    {showQuickAdd && (
      <PipelineLeadModal
        open={quickAddOpen}
        pipelineId={pipelineId}
        stageId={stage.id}
        stageName={stage.nome}
        onClose={() => setQuickAddOpen(false)}
      />
    )}
    </>
  );
}

const AGENDAMENTO_STATUS_LABEL: Record<string, string> = {
  PENDENTE: 'Pendente',
  CONFIRMADO: 'Confirmado',
  REALIZADO: 'Realizado',
  CANCELADO: 'Cancelado',
  FALTOU: 'Faltou',
  NOVA_TENTATIVA: 'Nova tentativa',
  REAGENDADO: 'Reagendado',
};

function PipelineCardTooltip({
  card,
  stageName,
  tagsById,
}: {
  card: PipelineCard;
  stageName?: string;
  tagsById: Map<string, PipelineTag>;
}) {
  const dados = card.dados_card || {};
  const contrato = card.contratos;
  const grupoTitulo = contrato?.grupos?.titulo || (contrato?.grupos?.numero_grupo ? `Grupo ${contrato.grupos.numero_grupo}` : '') || dados.grupo_titulo;
  const bemTipo = contrato?.bens?.tipo_bem || dados.bem_tipo;
  const clienteNome = contrato?.clientes?.nome || dados.cliente_nome || dados.nome || 'Sem nome';
  const nomeNegocio = typeof dados.nome_negocio === 'string' ? dados.nome_negocio.trim() : '';
  const clienteNomeDisponivel = Boolean(contrato?.clientes?.nome || dados.cliente_nome || dados.nome);
  const mostrarClienteAbaixo = clienteNomeDisponivel && Boolean(nomeNegocio || (grupoTitulo && bemTipo));
  const valor = contrato?.valor_bem || dados.valor;

  const telefone = typeof dados.telefone === 'string' ? formatPhone(dados.telefone) : null;
  const agendamentoLabel = formatAgendamentoCard(
    card.data_para_agendamento,
    typeof dados.hora_agendamento === 'string' ? dados.hora_agendamento : null
  );
  const statusAgendamento =
    typeof dados.status_agendamento === 'string'
      ? AGENDAMENTO_STATUS_LABEL[dados.status_agendamento] ?? dados.status_agendamento
      : null;
  const dataCriacao = card.data_criacao ?? card.created_at;
  const cardTagIds = Array.isArray(dados.tags) ? (dados.tags as string[]) : [];
  const tagNomes = cardTagIds.map((id) => tagsById.get(id)?.nome).filter((n): n is string => !!n);

  return (
    <div className="space-y-1.5 py-0.5">
      <p className="font-semibold text-sm text-white leading-tight">
        {nomeNegocio || (grupoTitulo && bemTipo
          ? `${grupoTitulo} - ${bemTipo}`
          : clienteNome)}
      </p>
      {mostrarClienteAbaixo && (
        <TooltipDetailRow label="Cliente" value={clienteNome} />
      )}
      {telefone && (
        <TooltipDetailRow label="Telefone" value={telefone} />
      )}
      {stageName && <TooltipDetailRow label="Etapa" value={stageName} />}
      {agendamentoLabel && (
        <TooltipDetailRow label="Agendamento" value={agendamentoLabel} />
      )}
      {statusAgendamento && (
        <TooltipDetailRow label="Status" value={statusAgendamento} />
      )}
      {dataCriacao && (
        <TooltipDetailRow label="Criado em" value={formatDate(dataCriacao)} />
      )}
      {dados.interesse && (
        <TooltipDetailRow label="Observações" value={String(dados.interesse)} />
      )}
      {valor != null && (
        <TooltipDetailRow
          label="Valor"
          value={`R$ ${Number(valor).toLocaleString('pt-BR')}`}
        />
      )}
      {tagNomes.length > 0 && (
        <TooltipDetailRow label="Tags" value={tagNomes.map((n) => `#${n}`).join(' ')} />
      )}
    </div>
  );
}

function TooltipDetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-xs leading-relaxed">
      <span className="text-white/50 shrink-0">{label}:</span>
      <span className="text-white/90">{value}</span>
    </div>
  );
}

function PipelineCardItem({
  card,
  stageName,
  tagsById,
  onClick,
  onDragStart,
  onDragEnd,
  accentColor = '#FF3737',
}: {
  card: PipelineCard;
  stageName?: string;
  tagsById: Map<string, PipelineTag>;
  onClick?: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  accentColor?: string;
}) {
  const dados = card.dados_card || {};
  const contrato = card.contratos;
  const grupoTitulo = contrato?.grupos?.titulo || (contrato?.grupos?.numero_grupo ? `Grupo ${contrato.grupos.numero_grupo}` : '') || dados.grupo_titulo;
  const bemTipo = contrato?.bens?.tipo_bem || dados.bem_tipo;
  const clienteNome = contrato?.clientes?.nome || dados.cliente_nome || dados.nome || 'Sem nome';
  const nomeNegocio = typeof dados.nome_negocio === 'string' ? dados.nome_negocio.trim() : '';
  const clienteNomeDisponivel = Boolean(contrato?.clientes?.nome || dados.cliente_nome || dados.nome);
  const mostrarClienteAbaixo = clienteNomeDisponivel && Boolean(nomeNegocio || (grupoTitulo && bemTipo));
  const valor = contrato?.valor_bem || dados.valor;

  const telefoneFormatado =
    typeof dados.telefone === 'string' ? formatPhone(dados.telefone) : null;
  const dataCriacao = card.data_criacao ?? card.created_at;
  const agendamentoLabel = formatAgendamentoCard(
    card.data_para_agendamento,
    typeof dados.hora_agendamento === 'string' ? dados.hora_agendamento : null
  );
  const cardTagIds = Array.isArray(dados.tags) ? (dados.tags as string[]) : [];

  return (
    <Tooltip
      title={<PipelineCardTooltip card={card} stageName={stageName} tagsById={tagsById} />}
      placement="right"
      mouseEnterDelay={0.35}
      styles={{ root: { maxWidth: 300 } }}
    >
      <div
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', card.id);
          onDragStart();
        }}
        onDragEnd={onDragEnd}
        className="cursor-grab active:cursor-grabbing transition-all duration-200"
      >
        <button
          type="button"
          onClick={onClick}
          className="w-full text-left p-3 rounded-lg bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 hover:shadow-lg hover:border-gray-300 dark:hover:border-zinc-600 hover:scale-105 transition-all duration-200 group active:scale-95 overflow-hidden"
          style={{
            borderLeft: `3px solid ${accentColor}`,
          }}
        >
          {/* Título: Nome do Negócio (se houver), senão Grupo - Bem, senão Nome do Cliente */}
          <h3 className="font-bold text-sm text-gray-900 dark:text-white group-hover:text-[#FF3737] truncate mb-2 leading-tight">
            {nomeNegocio || (grupoTitulo && bemTipo
              ? `${grupoTitulo} - ${bemTipo}`
              : clienteNome)}
          </h3>

          {cardTagIds.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-1.5">
              {cardTagIds.map((tagId) => {
                const tag = tagsById.get(tagId);
                if (!tag) return null;
                return (
                  <span
                    key={tagId}
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium leading-none"
                    style={{ background: `${tag.cor}22`, color: tag.cor }}
                  >
                    {tag.nome}
                  </span>
                );
              })}
            </div>
          )}

          {/* Subtitle: Cliente */}
          {mostrarClienteAbaixo && (
            <p className="text-xs text-gray-600 dark:text-gray-400 truncate mb-1.5">
              {clienteNome}
            </p>
          )}

          {telefoneFormatado && !mostrarClienteAbaixo && (
            <p className="text-xs text-gray-500 truncate mb-1.5">{telefoneFormatado}</p>
          )}

          {dados.interesse && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mb-1.5 line-clamp-2">{dados.interesse}</p>
          )}

          {valor != null && (
            <p className="text-sm font-bold mb-2 px-2 py-1 rounded bg-blue-50 dark:bg-blue-900/20" style={{ color: accentColor }}>
              R$ {Number(valor).toLocaleString('pt-BR')}
            </p>
          )}

          {(dataCriacao || agendamentoLabel) && (
            <div className="text-[10px] text-gray-400 dark:text-gray-500 pt-2 border-t border-gray-100 dark:border-zinc-700 space-y-0.5">
              {dataCriacao && (
                <p>
                  <span className="text-gray-300 dark:text-zinc-600">Criado · </span>
                  {formatDate(dataCriacao)}
                </p>
              )}
              {agendamentoLabel && (
                <p>
                  <span className="text-gray-300 dark:text-zinc-600">Agend. · </span>
                  {agendamentoLabel}
                </p>
              )}
            </div>
          )}
        </button>
      </div>
    </Tooltip>
  );
}

export default PipelineBoardKanban;
